import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CheckCircle, XCircle, Clock, MusicNotes } from '@phosphor-icons/react';

export default function CuratorInbox() {
  const [curatorFeed, setCuratorFeed] = useState([]);
  const [artistFeed, setArtistFeed] = useState([]);
  const [pendingNo, setPendingNo] = useState(null);

  const load = () => {
    api.get('/submissions/curator').then(r => setCuratorFeed(r.data));
    api.get('/submissions/artist').then(r => setArtistFeed(r.data));
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision, no_resolution = null) => {
    try {
      const payload = { decision };

      if (decision === 'no') {
        payload.no_resolution = no_resolution;
      }

      await api.post(`/submissions/${id}/decide`, payload);

      if (decision === 'yes') {
        toast.success('YES recorded.');
      } else if (decision === 'maybe') {
        toast.success('MAYBE recorded.');
      } else if (no_resolution === 'refund_artist') {
        toast.success('NO recorded — artist refunded.');
      } else {
        toast.success('NO recorded — payment kept.');
      }

      setPendingNo(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Curator Inbox</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">Decide fair.</h1>
        <p className="text-zinc-400 mt-1 text-sm">
          Review the submission quickly and choose YES, MAYBE or NO. Payment is for consideration, not playlist placement.
        </p>
      </header>

      <Tabs defaultValue="incoming" className="tabs-underline">
        <TabsList className="bg-transparent border-b border-zinc-800 rounded-none w-full justify-start h-auto p-0 gap-6">
          <TabsTrigger data-testid="tab-incoming" value="incoming" className="border-b-2 border-transparent rounded-none px-1 pb-3 font-mono text-xs uppercase text-zinc-400 data-[state=active]:bg-transparent">Incoming ({curatorFeed.filter(s => s.status === 'pending').length})</TabsTrigger>
          <TabsTrigger data-testid="tab-my-pitches" value="my" className="border-b-2 border-transparent rounded-none px-1 pb-3 font-mono text-xs uppercase text-zinc-400 data-[state=active]:bg-transparent">My pitches ({artistFeed.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="incoming" className="mt-6 space-y-3">
          {curatorFeed.length === 0 && <div className="text-sm text-zinc-500 py-6">Nothing pending.</div>}
          {curatorFeed.map(s => (
            <div key={s.submission_id} data-testid={`incoming-${s.submission_id}`} className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5 flex items-center gap-4 card-hover">
              <div className="w-12 h-12 rounded-lg border border-zinc-800 bg-zinc-950 flex items-center justify-center">
                <MusicNotes size={20} className="text-[#ccff00]" weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg">
                  {s.song?.title || 'Untitled'}
                  {s.song?.artist && (
                    <span className="text-zinc-500 font-normal text-sm"> by {s.song.artist}</span>
                  )}
                </div>
                <div className="text-xs font-mono text-zinc-500">To: {s.curator?.playlist_name} · {s.credits_reserved} credits paid for consideration</div>
                {s.message && <div className="text-sm text-zinc-400 mt-1 italic">"{s.message}"</div>}

                {s.promotion_mode === 'link' && s.promotion_url && (
                  <div className="mt-3 border border-zinc-800 rounded-lg p-3 bg-zinc-950/40">
                    <div className="flex items-start gap-3">
                      {s.song?.cover_url ? (
                        <img
                          src={s.song.cover_url}
                          alt=""
                          className="w-14 h-14 rounded-lg object-cover border border-zinc-800"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-lg border border-zinc-800 bg-zinc-950 flex items-center justify-center text-zinc-700">
                          <MusicNotes size={20} />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-zinc-100 truncate">
                          {s.song?.title || 'Untitled'}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1 truncate">
                          {s.song?.artist || s.artist?.name || 'Unknown artist'}
                        </div>

                        {(s.song?.genre ||
                          s.song?.subgenre ||
                          s.song?.microgenre) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {s.song?.genre && (
                              <span className="px-2 py-1 rounded border border-zinc-800 text-[10px] font-mono text-zinc-400">
                                Genre: {s.song.genre}
                              </span>
                            )}
                            {s.song?.subgenre && (
                              <span className="px-2 py-1 rounded border border-zinc-800 text-[10px] font-mono text-zinc-400">
                                Subgenre: {s.song.subgenre}
                              </span>
                            )}
                            {s.song?.microgenre && (
                              <span className="px-2 py-1 rounded border border-zinc-800 text-[10px] font-mono text-zinc-400">
                                Microgenre: {s.song.microgenre}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {(() => {
                      let playerSrc = null;

                      if (s.song?.spotify_url) {
                        const match = s.song.spotify_url.match(
                          /open\.spotify\.com\/(?:intl-[^/]+\/)?(track|album|playlist)\/([^?/#]+)/
                        );
                        if (match) {
                          playerSrc = `https://open.spotify.com/embed/${match[1]}/${match[2]}?utm_source=generator`;
                        }
                      } else if (s.song?.youtube_url) {
                        const match = s.song.youtube_url.match(
                          /(?:v=|youtu\.be\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/
                        );
                        if (match) {
                          playerSrc = `https://www.youtube.com/embed/${match[1]}`;
                        }
                      } else if (s.song?.soundcloud_url) {
                        playerSrc = `https://w.soundcloud.com/player/?url=${encodeURIComponent(s.song.soundcloud_url)}&color=%23ccff00&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&visual=false`;
                      }

                      return playerSrc ? (
                        <div className="mt-3 overflow-hidden rounded-lg border border-zinc-800 bg-black">
                          <iframe
                            title="Song preview"
                            src={playerSrc}
                            className="w-full h-[152px]"
                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                            loading="lazy"
                          />
                        </div>
                      ) : null;
                    })()}

                    <a
                      href={s.promotion_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block max-w-full truncate text-xs font-mono text-[#ccff00] hover:underline"
                      title={s.promotion_url}
                      data-testid={`promotion-url-${s.submission_id}`}
                    >
                      Open original link
                    </a>
                  </div>
                )}

                {s.promotion_mode === 'imported_audio' && (
                  <div className="mt-2 border border-zinc-800 rounded-lg p-3 bg-zinc-950/40">
                    <div className="text-[10px] font-mono uppercase text-zinc-500 mb-2">
                      Imported song
                    </div>

                    {s.song?.audio_url ? (
                      <audio
                        controls
                        preload="none"
                        src={s.song.audio_url}
                        className="w-full h-9"
                        data-testid={`promotion-audio-${s.submission_id}`}
                      />
                    ) : (
                      <div className="text-xs text-amber-400">
                        Imported audio is unavailable.
                      </div>
                    )}
                  </div>
                )}
              </div>
              {s.status === 'pending' ? (
                <div className="flex flex-col items-end gap-2">
                  <div className="flex gap-2">
                    <button
                      data-testid={`yes-${s.submission_id}`}
                      onClick={() => decide(s.submission_id, 'yes')}
                      className="btn-neon px-4 py-2 rounded-full text-sm font-display font-semibold flex items-center gap-1"
                    >
                      <CheckCircle size={14} weight="fill" /> YES
                    </button>

                    <button
                      data-testid={`maybe-${s.submission_id}`}
                      onClick={() => decide(s.submission_id, 'maybe')}
                      className="px-4 py-2 text-sm rounded-full border border-amber-500/40 text-amber-300 hover:border-amber-400/70 transition-colors"
                    >
                      MAYBE
                    </button>

                    <button
                      data-testid={`no-${s.submission_id}`}
                      onClick={() => setPendingNo(s.submission_id)}
                      className="px-4 py-2 text-sm rounded-full border border-zinc-800 hover:border-red-500/60 hover:text-red-400 transition-colors flex items-center gap-1"
                    >
                      <XCircle size={14} /> NO
                    </button>
                  </div>

                  {pendingNo === s.submission_id && (
                    <div className="flex flex-wrap items-center justify-end gap-2 rounded-lg border border-zinc-800 bg-zinc-950/80 p-2">
                      <span className="text-[10px] font-mono uppercase text-zinc-500 mr-1">
                        NO resolution
                      </span>

                      <button
                        data-testid={`refund-${s.submission_id}`}
                        onClick={() => decide(s.submission_id, 'no', 'refund_artist')}
                        className="px-3 py-1.5 text-xs rounded-full border border-[#ccff00]/40 text-[#ccff00] hover:border-[#ccff00] transition-colors"
                      >
                        Refund artist
                      </button>

                      <button
                        data-testid={`keep-payment-${s.submission_id}`}
                        onClick={() => decide(s.submission_id, 'no', 'keep_payment')}
                        className="px-3 py-1.5 text-xs rounded-full border border-zinc-700 text-zinc-300 hover:border-zinc-500 transition-colors"
                      >
                        Keep payment
                      </button>

                      <button
                        type="button"
                        onClick={() => setPendingNo(null)}
                        className="px-3 py-1.5 text-xs rounded-full text-zinc-500 hover:text-zinc-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <StatusPill status={s.status} />
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="my" className="mt-6 space-y-3">
          {artistFeed.length === 0 && <div className="text-sm text-zinc-500 py-6">You haven't pitched anything yet. Head to the Curator Marketplace.</div>}
          {artistFeed.map(s => (
            <div key={s.submission_id} data-testid={`mypitch-${s.submission_id}`} className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5 flex items-center gap-4">
              <img src={s.curator?.avatar} alt="" className="w-12 h-12 rounded-lg object-cover border border-zinc-800" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg">{s.curator?.playlist_name}</div>
                <div className="text-xs font-mono text-zinc-500">
                  Pitched: {s.song?.title} · {s.credits_reserved} credits
                </div>

                {s.promotion_mode === 'link' && s.promotion_url && (
                  <a
                    href={s.promotion_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block max-w-full truncate text-xs font-mono text-[#ccff00] hover:underline"
                    title={s.promotion_url}
                    data-testid={`promotion-url-${s.submission_id}`}
                  >
                    {s.promotion_url}
                  </a>
                )}

                {s.promotion_mode === 'imported_audio' && (
                  <div className="mt-2 text-xs font-mono text-zinc-500">
                    Source: imported song audio
                  </div>
                )}
              </div>
              <StatusPill status={s.status} />
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: { icon: Clock, cls: 'text-zinc-400 bg-zinc-800' },
    yes: { icon: CheckCircle, cls: 'text-[#ccff00] bg-[#ccff00]/20' },
    maybe: { icon: Clock, cls: 'text-amber-300 bg-amber-500/10' },
    no: { icon: XCircle, cls: 'text-zinc-500 bg-zinc-800' },
  };
  const { icon: Icon, cls } = map[status] || map.pending;
  return (
    <span className={`flex items-center gap-1 text-xs font-mono uppercase px-3 py-1 rounded ${cls}`}>
      <Icon size={12} /> {status}
    </span>
  );
}
